/**
 * Glass-styled section components with consistent styling.
 * Eliminates repetitive sx props for section/box styling across the application.
 */

import React from 'react';
import { Box, BoxProps, Typography, TypographyProps } from '@mui/material';
import { BG_COLORS, BORDER_COLORS, TEXT_COLORS } from '../../utils/colors';

// ============================================
// GLASS SECTION
// ============================================

interface GlassSectionProps extends BoxProps {
  /**
   * Use compact padding (p: 2 instead of p: 3)
   */
  compact?: boolean;
  /**
   * Use lighter background
   */
  light?: boolean;
}

/**
 * Glass-styled section container.
 * Replaces repetitive pattern:
 * sx={{ p: 3, background: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}
 */
export const GlassSection: React.FC<GlassSectionProps> = ({
  compact = false,
  light = false,
  sx,
  children,
  ...props
}) => {
  return (
    <Box
      {...props}
      sx={{
        p: compact ? 2 : 3,
        background: light ? BG_COLORS.GLASS : BG_COLORS.GLASS_DARK,
        borderRadius: compact ? 1.5 : 2,
        border: `1px solid ${BORDER_COLORS.DEFAULT}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// ============================================
// GLASS STAT BOX
// ============================================

interface GlassStatBoxProps extends BoxProps {
  /**
   * Icon element to display
   */
  icon?: React.ReactNode;
}

/**
 * Glass stat box with icon and value layout.
 * Used for displaying stats like: [icon] Value
 */
export const GlassStatBox: React.FC<GlassStatBoxProps> = ({
  icon,
  sx,
  children,
  ...props
}) => {
  return (
    <Box
      {...props}
      sx={{
        p: 2,
        background: BG_COLORS.GLASS_DARK,
        borderRadius: 1.5,
        border: `1px solid ${BORDER_COLORS.DEFAULT}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: BG_COLORS.GLASS_LIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
      {children}
    </Box>
  );
};

// ============================================
// SECTION CAPTION
// ============================================

interface SectionCaptionProps extends TypographyProps {
  /**
   * Margin bottom spacing
   */
  mb?: number;
}

/**
 * Section caption - uppercase label with letter spacing.
 * Replaces repetitive pattern:
 * sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, display: 'block' }}
 */
export const SectionCaption: React.FC<SectionCaptionProps> = ({
  mb = 0,
  sx,
  children,
  ...props
}) => {
  return (
    <Typography
      variant="caption"
      {...props}
      sx={{
        color: TEXT_COLORS.CAPTION,
        textTransform: 'uppercase',
        letterSpacing: 1,
        fontWeight: 600,
        display: 'block',
        mb,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

// ============================================
// ICON CIRCLE
// ============================================

interface IconCircleProps extends BoxProps {
  /**
   * Size of the circle (default: 48)
   */
  size?: number | 'small' | 'medium' | 'large';
  /**
   * Background color variant
   */
  variant?: 'default' | 'success' | 'error' | 'warning';
}

const ICON_CIRCLE_SIZES = {
  small: 36,
  medium: 48,
  large: 64,
};

const ICON_CIRCLE_VARIANTS: Record<string, string> = {
  default: BG_COLORS.GLASS_LIGHT,
  success: BG_COLORS.SUCCESS_LIGHT,
  error: BG_COLORS.ERROR_LIGHT,
  warning: BG_COLORS.WARNING_LIGHT,
};

/**
 * Circular container for icons.
 */
export const IconCircle: React.FC<IconCircleProps> = ({
  size = 'medium',
  variant = 'default',
  sx,
  children,
  ...props
}) => {
  const actualSize = typeof size === 'number' ? size : ICON_CIRCLE_SIZES[size];
  const background = ICON_CIRCLE_VARIANTS[variant] || ICON_CIRCLE_VARIANTS.default;

  return (
    <Box
      {...props}
      sx={{
        width: actualSize,
        height: actualSize,
        borderRadius: '50%',
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// ============================================
// INFO ROW
// ============================================

interface InfoRowProps extends BoxProps {
  /**
   * Label text
   */
  label: string;
  /**
   * Value to display
   */
  value: React.ReactNode;
}

/**
 * Info row with label and value - commonly used in detail views.
 */
export const InfoRow: React.FC<InfoRowProps> = ({
  label,
  value,
  sx,
  ...props
}) => {
  return (
    <Box
      {...props}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1,
        borderBottom: `1px solid ${BORDER_COLORS.DEFAULT}`,
        '&:last-child': {
          borderBottom: 'none',
        },
        ...sx,
      }}
    >
      <Typography variant="body2" sx={{ color: TEXT_COLORS.SECONDARY }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: TEXT_COLORS.LIGHT, fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
};

// ============================================
// GLASS LIST ITEM
// ============================================

interface GlassListItemProps extends BoxProps {
  /**
   * Active/selected state
   */
  active?: boolean;
  /**
   * Success variant (green background)
   */
  success?: boolean;
}

/**
 * Glass-styled list item.
 * Replaces repetitive pattern:
 * sx={{ p: 1, mb: 1, background: 'rgba(255, 255, 255, 0.08)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.12)' }}
 */
export const GlassListItem: React.FC<GlassListItemProps> = ({
  active = false,
  success = false,
  sx,
  children,
  ...props
}) => {
  const isHighlighted = active || success;

  return (
    <Box
      {...props}
      sx={{
        p: 1,
        mb: 1,
        background: isHighlighted ? BG_COLORS.SUCCESS_LIGHT : BG_COLORS.GLASS,
        borderRadius: 1,
        border: `1px solid ${isHighlighted ? BORDER_COLORS.SUCCESS : BORDER_COLORS.LIGHT}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
